import React from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

interface DeleteChannelConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  channelName?: string;
}

export const DeleteChannelConfirmModal: React.FC<
  DeleteChannelConfirmModalProps
> = ({ isOpen, onClose, onConfirm, isLoading, channelName }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogOverlay />
      <DialogContent
        showCloseButton={false}
        className="border-base-border w-[425px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[425px]"
      >
        <DialogHeader className="border-base-border relative gap-2 border-b px-6 py-4 pr-14 text-left">
          <DialogTitle className="text-base leading-normal font-semibold text-black/96">
            {t("chats.deleteChannelTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 text-black/65">
            {t("chats.deleteChannelDescription", { name: channelName })}
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              className="absolute top-3 right-4 rounded-md text-black/55"
              disabled={isLoading}
              aria-label={t("chats.closeDialog")}
            >
              <X />
            </Button>
          </DialogClose>
        </DialogHeader>
        <DialogFooter className="flex-row justify-end gap-2 px-6 py-4">
          <Button
            type="button"
            variant="new_outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("chats.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_destructive"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? t("chats.deleting") : t("chats.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
